<template>
  <div class="confirmation__row px-2 md:px-8 xl:px-16">
    <h3 class="confirmation__title">
      {{ t('confirmationComp.title') }}
    </h3>
  </div>

  <div class="confirmation__row px-2 md:px-8 xl:px-16">
    {{ t('confirmationComp.descr') }}
  </div>

  <div class="confirmation__row px-2 md:px-8 xl:px-16 flex-col !grow overflow-auto">
    <label v-for="(item, index) in form.getFields()" class="confirmation__group" :key="index">
      <div class="label text_bold text_size_medium">{{ item.title }}</div>
      <template v-for="field in item.values" :key="field.key">
        <template v-if="!field.hidden">
          <template v-if="typeof field.type === 'object'">
            <component :data-testid="field.dataTestId" :is="field.type" v-bind="field.props || {}" v-model="field.value"
                       @update:modelValue="($event) => (field.handler ? field.handler($event) : null)"/>
          </template>
          <input
              v-else
              v-maska:[field.mask]
              v-model="field.value"
              class="confirmation__input"
              :class="field.className || ''"
              :type="field.type || 'text'"
              :placeholder="field.label"
              :data-testid="field.dataTestId"
              @change="($event) => (field.handler ? field.handler($event) : null)"
              @input="($event) => validateFIOField(field, $event.target.value)"
          />
          <p v-if="field.title">{{ field.title }}</p>
          <p class="error" v-if="field?.error">{{ t('confirmationComp.fieldNecessary') }}</p>
          <p class="error" v-if="field?.validationError">{{ field.validationError }}</p>
        </template>
      </template>
    </label>
    <Spinner v-if="snilsAnalysisInProgress" size="3rem"/>
    <div v-else-if="!files.snils_scan.hidden && !snilsAnalysisInProgress" class="confirmation__group">
      <template v-if="!files.snils_scan.file">
        <div class="label text_bold text_size_medium" :class="[snilsError ? 'text_color_red' : 'text-slate-600']">
          {{ t('confirmationComp.snils') }}
        </div>
        <label class="confirmation__file-input">
          <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png"
              @change="handleFileSelect($event, 'snils_scan')"
          />
          <btn-wrapper
              data-testid="contract-register-attach-snils"
              :params="{
              class: 'confirmation__input flex cursor-pointer',
              type: 'div'
            }"
          >
            <div class="mr-4 flex items-center">
              <svg class="icon">
                <use xlink:href="/img/sprites/icons-sprite.svg#icon-file"></use>
              </svg>
            </div>
            {{ t('confirmationComp.attachBtn') }}
          </btn-wrapper
          >
        </label>
        <p class="error" v-if="files.snils_scan.error">{{ t('confirmationComp.fieldNecessary') }}</p>
        <p class="text-xs text-gray-500 mt-1">{{ t('confirmationComp.formatAccept') }}</p>
        <p class="text-xs text-gray-500">{{ t('confirmationComp.maxSizeFile') }}</p>
      </template>
      <div v-else class="confirmation__document" data-testid="contract-register-attached-file">
        <i class="confirmation__icon">
          <svg class="icon icon_color_blue">
            <use xlink:href="/img/sprites/icons-sprite.svg#icon-file"></use>
          </svg>
        </i>
        <div class="confirmation__file">{{ files.snils_scan.file.name }}</div>
        <svg class="icon confirmation__delete" @click="Object.assign(files.snils_scan, { file: null, error: null })">
          <use xlink:href="/img/sprites/icons-sprite.svg#icon-close"></use>
        </svg>
      </div>
    </div>
    <div v-if="snilsWarningMessage"
         class="relative flex flex-col items-center gap-2 p-4 pr-10 mb-4 text-sm text-yellow-700 bg-yellow-200 rounded-lg confirmation__group__warning_content">
      <div>
        <span>{{ snilsWarningMessage }}</span>
        <strong class="block text-center pt-1">{{ t('confirmationComp.snilsAnalysisTolerate') }}</strong>
      </div>
      <svg class="icon confirmation__warn_clear absolute top-4 right-4" @click="() => snilsWarningMessage = ''">
        <use xlink:href="/img/sprites/icons-sprite.svg#icon-close"></use>
      </svg>
    </div>
    <Marker data-testid="contract-register-checkbox1" v-model="restrictions.policy2"
            @change="restrictions.policy2 = !restrictions.policy2" type="checkbox"
            class="form__checkbox w-full pb-4">
      {{ t('confirmationComp.confirmation.reliability') }}
    </Marker>
    <Marker data-testid="contract-register-checkbox2" v-model="restrictions.policy1"
            @change="restrictions.policy1 = !restrictions.policy1" type="checkbox"
            class="form__checkbox">
      {{ t('confirmationComp.confirmation.personal', { hostname }) }}
    </Marker>
  </div>

  <div class="confirmation__row px-2 md:px-8 xl:px-16">
    <btn-wrapper
        @click="registerContract"
        data-testid="contract-register-send-register-request"
        :params="{
        class: 'confirmation__button confirmation__button_next w-full',
        type: 'button',
        title: !registrationProcessing ? t('confirmationComp.registerBtn') : '',
        isSlotIsset: true,
        disabled: !restrictions.policy1 || !restrictions.policy2  || registrationProcessing || !snilsAnalyzed
      }"
    >
      <div v-if="registrationProcessing" role="status">
        <svg aria-hidden="true" class="inline w-6 h-6 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-sky-400"
             viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"/>
          <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"/>
        </svg>
        <span class="sr-only">{{ t('confirmationComp.loading') }}</span>
      </div>
    </btn-wrapper>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { ref, shallowRef, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { vMaska } from 'maska'
import Marker from '@/components/UI/marker/index.vue'
import useFormProcessor from './mixin'
import { buildPaidcoursesMetaData } from '@/script/paidcoursesMeta'
import Spinner from '@/components/Spinner.vue'

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const toast = useToast()
const registrationProcessing = ref(false)
const snilsAnalysisInProgress = ref(false)
const snilsError = ref(false)
const snilsAnalyzed = ref(false)
const snilsWarningMessage = ref('')
const snilsAnalysisId = ref(null)

const hostname = computed(() => window.location.hostname)
const userFIO = computed(() => store.getters.user?.fullname.split(' ') || [])

const form = useFormProcessor()
const files = form.addFiles('snils_scan')
const restrictions = form.addRestrictions('policy1')

/**
 * Валидация русского языка для ФИО
 * @param {string} value
 * @returns {string|null}
 */
const validateRussianText = (value) => {
  if (!value) return null
  const regex = /^[а-яА-ЯёЁ\s\-']+$/

  if (!regex.test(value)) {
    return t('confirmationComp.validateText')
    //return 'Допустимы только русские буквы';
  }

  return null
}

/**
 * Валидация поля ФИО
 * @param {Object} field
 * @param {string} value
 */
const validateFIOField = (field, value) => {
  if (!isForeigner.value && ['lastname', 'firstname', 'middlename'].includes(field.key)) {
    field.validationError = validateRussianText(value)
  } else {
    field.validationError = null
  }
}

/**
 * Обновление валидации всех полей ФИО
 */
const updateFIOValidation = () => {
  const fioFields = fields.value[0].values.slice(0, 3)
  fioFields.forEach(field => {
    validateFIOField(field, field.value)
  })
}

const fields = form.addFields([
  {
    title: t('confirmationComp.fields.passport.title'),
    key: 'passport_info',
    values: [
      {
        key: 'lastname',
        value: userFIO.value[0] || '',
        label: t('confirmationComp.fields.surname'), // "Фамилия",
        dataTestId: 'contract-register-surname'
      },
      {
        key: 'firstname',
        value: userFIO.value[1] || '',
        label: t('confirmationComp.fields.name'), // "Имя",
        dataTestId: 'contract-register-firstname'
      },
      {
        key: 'middlename',
        value: userFIO.value[2] || '',
        optional: true,
        label: t('confirmationComp.fields.middlename'), // "Отчество",
        dataTestId: 'contract-register-middlename'
      },
      {
        key: 'foreigner',
        value: false,
        type: shallowRef(Marker),
        className: 'form__checkbox !w-8',
        dataTestId: 'contract-register-foreigner-checkbox',
        props: {
          label: t('confirmationComp.fields.passport.foreigner'), // 'Являюсь иностранным гражданином',
          class: 'mb-3'
        },
        handler: (isForeigner) => {
          fields.value[0].values[4].mask = !isForeigner ? { mask: '####' } : undefined
          fields.value[0].values[5].mask = !isForeigner ? { mask: '######' } : undefined
          files.snils_scan.hidden = !!isForeigner

          const snils = form.field.snils
          if (isForeigner) {
            fields.value[2].values[0].mask = {
              mask: '+###########00',
              tokens: { 0: { pattern: /[0-9]/, optional: true } }
            } // ** phone number
            Object.assign(snils, { value: '', hidden: true, optional: true })
          } else {
            fields.value[2].values[0].mask = { mask: '+# (###) ### ####' } // ** phone number
            Object.assign(snils, { value: '', hidden: false, optional: false })
          }

          updateFIOValidation()
        }
      },
      {
        key: 'passportseries',
        value: '',
        label: t('confirmationComp.fields.passport.field1'), // "Серия паспорта",
        mask: { mask: '####' },
        dataTestId: 'contract-register-passportseries'
      },
      {
        key: 'passportnumber',
        value: '',
        label: t('confirmationComp.fields.passport.field2'), // "Номер паспорта",
        mask: { mask: '######' },
        dataTestId: 'contract-register-passportnumber'
      },
      {
        key: 'passportdate',
        value: '',
        label: t('confirmationComp.fields.passport.field3'), // "Дата выдачи паспорта"
        mask: { mask: '##-##-####' },
        dataTestId: 'contract-register-passportdate'
      }
    ]
  },
  {
    title: t('confirmationComp.fields.birthdate'), // "Дата рождения",
    key: 'birthdate',
    values: [
      {
        key: 'birthdate',
        value: '',
        label: t('confirmationComp.dateformat'), // "гггг-мм-дд",
        mask: { mask: '##-##-####' },
        dataTestId: 'contract-register-birthdate'
      }
    ]
  },
  {
    title: t('confirmationComp.fields.phone'), // "Номер телефона",
    key: 'phone',
    values: [
      {
        key: 'phone',
        value: '',
        label: '+X (XXX) XXX XXXX ',
        mask: { mask: '+# (###) ### ####' },
        dataTestId: 'contract-register-phone-number'
      }
    ]
  },
  {
    title: t('confirmationComp.fields.snils'), // "Снилс",
    key: 'snils',
    values: [
      {
        key: 'snils',
        value: '',
        label: 'XXX-XXX-XXX XX',
        mask: { mask: '###-###-### ##' },
        dataTestId: 'contract-register-snils-number'
      }
    ]
  }
])

const isForeigner = computed(() => fields.value[0].values[3].value)

/**
 * Обработчик выбора файла с валидацией типа и размера
 */
const handleFileSelect = async (event, fileName) => {
  const file = event.target.files[0]
  if (!file) return
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png'
  ]
  const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png']
  const maxFileSize = 8 * 1024 * 1024

  const fileExtension = '.' + file.name.toLowerCase().split('.').pop()
  const isValidType = allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)
  const isValidSize = file.size <= maxFileSize

  if (!isValidType) {
    toast.error(t('confirmationComp.errorFormatFile'))
    files[fileName].file = null
    event.target.value = ''
    return
  }

  if (!isValidSize) {
    toast.error(t('confirmationComp.errorSizeFile'))
    files[fileName].file = null
    event.target.value = ''
    return
  }

  files[fileName].file = file
  files[fileName].error = null
  await submitSnilsToAnalyze(fileName)
}

/**
 * Sends snils image for analysis by service
 * @param fileName {String} - the index of file in file storage(see useFormProcessor from './mixin')
 * @returns {Promise<void>}
 */
const submitSnilsToAnalyze = async (fileName) => {
  const file = files[fileName].file
  if (!file) {
    toast.error(t('files.noFileSelected'))
    return
  }
  snilsAnalysisInProgress.value = true
  snilsWarningMessage.value = ''
  store.dispatch('aidocsanner/analyzeFile', { file }).then((resp) => {
    const data = resp.data
    if (data.service_enabled) {
      snilsAnalysisId.value = data.prediction_id
      if (data.image_type !== 'Snils') {
        snilsWarningMessage.value += t('confirmationComp.snilsAnalysisFailed') + '\n'
      }
      if (!data.extracted_text) {
        snilsWarningMessage.value += t('confirmationComp.snilsAnalysisTextExtractionFailed')
      } else {
        if (form.field.snils.value !== data.extracted_text) {
          snilsWarningMessage.value += t('confirmationComp.snilsNumberDiffers')
        }
      }
    }
  }).catch((resp) => {
    console.error(resp.data.message)
  }).finally(() => {
    snilsAnalyzed.value = true
    snilsAnalysisInProgress.value = false
  })
}

const registerContract = async () => {
  if (!isForeigner.value) {
    const fioFields = fields.value[0].values.slice(0, 3)
    const hasValidationErrors = fioFields.some(field => field.validationError)

    if (hasValidationErrors) {
      toast.error(t('confirmationComp.fixErrorFIO'))
      return
    }
  }

  if (!form.validate(fields.value, files)) return

  const formData = form.getPayload()
  formData.birthdate = convertDateToServerFormat(formData.birthdate)
  formData.passportdate = convertDateToServerFormat(formData.passportdate)

  formData.foreigner = formData.foreigner ? 1 : 0
  formData.snilsAnalysisId = snilsAnalysisId.value
  const payload = Object.assign({
    options: {
      courseid: route.params.courseId || '',
      redirect_url: document.location.href
    },
    metaData: JSON.stringify(buildPaidcoursesMetaData({
      route,
      component: 'RegisterContract',
      method: 'registerContract',
      action: 'register_contract',
      triggerSource: 'confirmation_register_submit',
      surface: 'contract_registration_modal',
      courseId: route.params.courseId
    }))
  }, formData)

  try {
    registrationProcessing.value = true

    const resp = await store.dispatch('registerContract', payload)
    if (resp.affectedRows) {
      store.commit('setUserContract', null)

      toast.success(resp.message || t('confirmationComp.bidConfirm'))
      store.commit('setIsActiveConfirm', false)
    } else if (resp.message) {
      toast.error(resp.message)
    }
  } catch (err) {
    if (err.response?.data?.message) {
      toast.error(err.response.data?.message)
    } else {
      toast.error(t('confirmationComp.errorWhileSend'))
    }
  } finally {
    registrationProcessing.value = false
  }
}

/**
 * Конвертация даты в серверый формат.
 * Пример: из 30-01-2001 в 2001-01-30
 *
 * @param {string} dateStr
 * @returns {string}
 */
const convertDateToServerFormat = (dateStr) => {
  if (!dateStr) return ''

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr
  }

  const ddMmYyyyRegex = /^(\d{2})-(\d{2})-(\d{4})$/
  const match = dateStr.match(ddMmYyyyRegex)

  if (match) {
    const [_, day, month, year] = match

    const dayNum = parseInt(day, 10)
    const monthNum = parseInt(month, 10)

    if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31) {
      const formattedMonth = month.toString().padStart(2, '0')
      const formattedDay = day.toString().padStart(2, '0')

      return `${year}-${formattedMonth}-${formattedDay}`
    } else {
      console.error(t('confirmationComp.invalidDate'), dateStr)
      return ''
    }
  }

  try {
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0]
    }
  } catch (e) {
    console.error(t('confirmationComp.errorWhileParsingDate'), dateStr, e)
  }

  console.error(t('confirmationComp.errorTransformDate'), dateStr)
  return ''
}
</script>

<style scoped lang="scss">
@import "../scss/confirmation.scss";

.label {
  padding-bottom: 12px;
}

.error {
  color: #ff350d;
  font-size: 12px;
  margin-bottom: 0.5rem;
}

span {
  white-space: pre-line;
}
</style>
